import { useAdminAuth } from "@/contexts/useAdminAuth";
import { api } from "@/lib/api";
import { FLAG_DEFAULTS } from "@a7y/types";
import type { FeatureFlagName } from "@a7y/types";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook that returns whether a feature flag is enabled in the admin dashboard.
 *
 * Resolution order (first match wins):
 * 1. Query param override — `?v2=true` works in all environments
 * 2. Env var override — `VITE_FF_V2=true` for local dev builds
 * 3. API response — fetched from `/admin/feature-flags`
 * 4. Default — `FLAG_DEFAULTS[name]` (false)
 */
export function useFeatureFlag(name: FeatureFlagName): boolean {
  // Hooks must be called unconditionally (Rules of Hooks)
  // A7Y-4957: `/admin/feature-flags` is superadmin-only; a limited admin
  // resolves every flag from the overrides and defaults below.
  const { isSuperAdmin } = useAdminAuth();
  const { data } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: api.getFeatureFlags,
    staleTime: 60_000,
    enabled: isSuperAdmin,
  });

  // 1. Query param override
  const params = new URLSearchParams(window.location.search);
  const queryOverride = params.get(name.toLowerCase());
  if (queryOverride !== null) return queryOverride === "true";

  // 2. Env var override
  const envKey = `VITE_FF_${name}`;
  const envOverride = (import.meta.env as Record<string, string | undefined>)[
    envKey
  ];
  if (envOverride !== undefined) return envOverride === "true";

  // 3. API response
  if (data?.flags && name in data.flags) {
    return data.flags[name];
  }

  // 4. Default
  return FLAG_DEFAULTS[name];
}
